import React, { useState, useCallback, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Printer, FileSpreadsheet, Mail, Save, Columns3 } from "lucide-react";
import { REPORT_TYPES, CURRENCY_KEYS } from "@/config/reportOptions";
import { formatCurrency } from "@/lib/format";
import { C, Panel, PieCard, BarCard, LineCard } from "./reportsUi";

const CHART_TYPES = [["table", "Table only"], ["bar", "Bar chart"], ["pie", "Pie chart"], ["line", "Line chart"]];

export default function BuilderTab({ params, state, onChange, onEmail, onSaveView }) {
  const reportType = state.report_type || "lead_source";
  const chartType = state.chart_type || "table";
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/run", { params: { type: reportType, ...params } });
      setReport(data);
      const keys = (data.columns || []).map((c) => c.key);
      onChange((s) => ({ ...s, columns: (s.columns && s.columns.filter((k) => keys.includes(k)).length) ? s.columns : keys }));
    } catch { toast.error("Could not load report."); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, [reportType, params]);
  useEffect(() => { load(); }, [load]);

  const allCols = report?.columns || [];
  const visible = (state.columns && state.columns.length) ? state.columns : allCols.map((c) => c.key);
  const shownCols = allCols.filter((c) => visible.includes(c.key));
  const numericCols = useMemo(() => allCols.filter((c) => (report?.rows || []).some((r) => typeof r[c.key] === "number")), [allCols, report]);
  const valueCol = state.value_col && shownCols.some((c) => c.key === state.value_col) ? state.value_col : (numericCols[0]?.key || null);
  const labelCol = shownCols[0]?.key;

  const fmt = (key, val) => (CURRENCY_KEYS.has(key) ? formatCurrency(Number(val) || 0) : (val === "" || val == null ? "—" : String(val)));

  const chartData = useMemo(() => {
    if (!report || !valueCol || !labelCol) return [];
    return report.rows.map((r) => ({ name: String(r[labelCol]), value: Number(r[valueCol]) || 0 })).filter((x) => x.value !== 0).slice(0, 12);
  }, [report, valueCol, labelCol]);

  const toggleCol = (key) => onChange((s) => {
    const cur = (s.columns && s.columns.length) ? s.columns : allCols.map((c) => c.key);
    return { ...s, columns: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] };
  });

  const exportCsv = () => {
    if (!report) return;
    const head = shownCols.map((c) => `"${c.label}"`).join(",");
    const body = report.rows.map((r) => shownCols.map((c) => `"${String(r[c.key] == null ? "" : r[c.key]).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([`${head}\n${body}`], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `${report.type}_report.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 20000); toast.success("CSV exported.");
  };
  const esc = (v) => String(v == null ? "" : v).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const print = () => {
    if (!report) return;
    const w = window.open("", "_blank"); if (!w) { toast.error("Allow pop-ups to print."); return; }
    const head = shownCols.map((c) => `<th>${esc(c.label)}</th>`).join("");
    const rows = report.rows.map((r) => `<tr>${shownCols.map((c) => `<td>${esc(fmt(c.key, r[c.key]))}</td>`).join("")}</tr>`).join("");
    w.document.write(`<html><head><title>${esc(report.title)}</title><style>body{font-family:Arial;padding:28px;color:#252221}h1{color:#1C4358;font-size:20px}table{width:100%;border-collapse:collapse;font-size:12px;margin-top:10px}th,td{border:1px solid #E6EDF2;padding:6px 9px;text-align:left}th{background:#F4F8FB;color:#1C4358}</style></head><body><h1>MovingMyMama — ${esc(report.title)}</h1><div style="font-size:12px;color:#6b7280">Generated ${new Date().toLocaleString()}</div><table><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table><script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`);
    w.document.close();
  };

  return (
    <div className="space-y-4" data-testid="rc-builder">
      <Panel testid="builder-controls">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[11px] mb-1 block flex items-center gap-1" style={{ color: C.slate }}><FileSpreadsheet size={13} /> Report category</Label>
            <Select value={reportType} onValueChange={(v) => onChange((s) => ({ ...s, report_type: v, columns: [] }))}>
              <SelectTrigger className="h-9 w-[230px] text-[13px]" data-testid="builder-type"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[360px]">{REPORT_TYPES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] mb-1 block" style={{ color: C.slate }}>Chart type</Label>
            <Select value={chartType} onValueChange={(v) => onChange((s) => ({ ...s, chart_type: v }))}>
              <SelectTrigger className="h-9 w-[150px] text-[13px]" data-testid="builder-charttype"><SelectValue /></SelectTrigger>
              <SelectContent>{CHART_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {chartType !== "table" && numericCols.length > 0 && (
            <div>
              <Label className="text-[11px] mb-1 block" style={{ color: C.slate }}>Value column</Label>
              <Select value={valueCol || ""} onValueChange={(v) => onChange((s) => ({ ...s, value_col: v }))}>
                <SelectTrigger className="h-9 w-[170px] text-[13px]" data-testid="builder-valuecol"><SelectValue /></SelectTrigger>
                <SelectContent>{numericCols.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!report} data-testid="builder-csv"><Download size={15} className="mr-1.5" /> CSV</Button>
            <Button variant="outline" size="sm" onClick={print} disabled={!report} data-testid="builder-print"><Printer size={15} className="mr-1.5" /> Print</Button>
          </div>
        </div>
        {allCols.length > 0 && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: C.line }}>
            <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold" style={{ color: `${C.navy}B3` }}><Columns3 size={13} /> VISIBLE COLUMNS</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              {allCols.map((c) => (
                <label key={c.key} className="flex items-center gap-1.5 text-[12.5px] cursor-pointer" style={{ color: C.charcoal }}>
                  <Checkbox checked={visible.includes(c.key)} onCheckedChange={() => toggleCol(c.key)} data-testid={`builder-col-${c.key}`} /> {c.label}
                </label>
              ))}
            </div>
          </div>
        )}
      </Panel>

      {chartType !== "table" && (
        chartType === "pie" ? <PieCard title={`${report?.title || "Report"} — chart`} data={chartData} testid="builder-chart" />
          : chartType === "line" ? <LineCard title={`${report?.title || "Report"} — chart`} data={chartData} testid="builder-chart" />
            : <BarCard title={`${report?.title || "Report"} — chart`} data={chartData} money={CURRENCY_KEYS.has(valueCol)} color={C.green} testid="builder-chart" />
      )}

      <Panel title={report?.title || "Report"} testid="builder-output"
        right={<div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => onSaveView()} data-testid="builder-saveview" className="h-8 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Save size={14} /> Save view</Button>
          <Button size="sm" onClick={() => onEmail()} data-testid="builder-email" className="h-8 gap-1 bg-[#1C4358] hover:bg-[#16374a] text-white"><Mail size={14} /> Email this report</Button>
        </div>}>
        {loading ? (
          <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : !report || report.rows.length === 0 ? (
          <p className="text-[13px] py-8 text-center" style={{ color: C.slate }}>No data for this report and the selected filters.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border" style={{ borderColor: C.line }}>
            <table className="w-full border-collapse">
              <thead><tr style={{ background: "#F4F8FB" }}>{shownCols.map((c) => <th key={c.key} className="text-left font-semibold px-3 py-2 text-[12px] uppercase tracking-wide whitespace-nowrap" style={{ color: C.slate }}>{c.label}</th>)}</tr></thead>
              <tbody>
                {report.rows.map((r, idx) => (
                  <tr key={idx} className="border-t" style={{ borderColor: C.line }} data-testid="builder-row">
                    {shownCols.map((c) => <td key={c.key} className="px-3 py-2 text-[13px] tabular-nums whitespace-nowrap" style={{ color: C.charcoal }}>{fmt(c.key, r[c.key])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {report && report.rows.length > 0 && <p className="text-[11.5px] mt-2" style={{ color: C.slate }}>{report.rows.length} row{report.rows.length === 1 ? "" : "s"} · {shownCols.length}/{allCols.length} columns</p>}
      </Panel>
    </div>
  );
}
